// ============================================================================
// Meta Conversions API (server-side) — côté CLIENT.
//   Le navigateur envoie les events serveur au relais /api/capi (qui détient
//   l'access token et parle à graph.facebook.com). On ne fait ici que :
//     • générer un event_id partagé Pixel <-> serveur (déduplication) ;
//     • récupérer les cookies _fbp / _fbc (matching Meta) ;
//     • POSTer vers /api/capi (fire-and-forget, ne bloque jamais le funnel).
//
//   Déduplication : pour un même event_name, le Pixel navigateur ET l'event
//   serveur doivent porter le MÊME event_id, sinon Meta compte deux fois.
// ============================================================================

import { getStoredUtms } from "./utm";

export type MetaUserData = {
  email?: string;
  phone?: string;
  external_id?: string;
  fbp?: string;
  fbc?: string;
};

// --- event_id partagé -------------------------------------------------------

function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* pas de crypto.randomUUID (vieux navigateur / http) : fallback ci-dessous */
  }
  // Fallback suffisant pour une clé de déduplication.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * event_id du PageView, stable pour LE chargement de page courant.
 * Idempotent et indépendant de l'ordre d'init (le Pixel et l'event serveur
 * appellent tous deux ce getter → ils obtiennent la même valeur → dédup OK).
 * En SPA (navigation sans reload) l'id persiste, mais le Pixel ne refire pas
 * PageView sur les changements de route → un seul PageView par chargement.
 */
export function getPageViewEventId(): string {
  if (typeof window === "undefined") return "";
  const w = window as unknown as { __maPvId?: string };
  if (!w.__maPvId) w.__maPvId = uuid();
  return w.__maPvId;
}

/** Nouvel event_id (pour un event de conversion ponctuel : Lead, etc.). */
export function newEventId(): string {
  return uuid();
}

// --- cookies Meta -----------------------------------------------------------

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : undefined;
}

/**
 * Cookies _fbp / _fbc. Si _fbc est absent mais qu'on a un fbclid (URL ou UTM
 * stockés), on le reconstruit au format attendu par Meta : fb.1.<ts>.<fbclid>.
 */
export function getFbCookies(): { fbp?: string; fbc?: string } {
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");

  if (!fbc) {
    let fbclid: string | undefined;
    try {
      fbclid = new URLSearchParams(window.location.search).get("fbclid") ?? undefined;
    } catch {
      /* ignore */
    }
    fbclid = fbclid || getStoredUtms().fbclid;
    if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
  }

  return { fbp, fbc };
}

// --- envoi vers le relais ---------------------------------------------------

/**
 * Envoie un event serveur (fire-and-forget). Le relais /api/capi complète
 * user_data avec l'IP, le User-Agent et les cookies _fbp/_fbc lus côté serveur,
 * et hashe email/téléphone en SHA-256. On lui transmet _fbp/_fbc côté client
 * en secours (ils peuvent manquer dans les headers selon le contexte).
 */
export function sendServerEvent(
  eventName: string,
  opts: {
    eventId: string;
    userData?: MetaUserData;
    customData?: Record<string, unknown>;
    actionSource?: string;
  }
): void {
  if (typeof window === "undefined") return;

  const { fbp, fbc } = getFbCookies();
  const body = {
    event_name: eventName,
    event_id: opts.eventId,
    event_source_url: window.location.href,
    action_source: opts.actionSource ?? "website",
    user_data: { fbp, fbc, ...opts.userData },
    custom_data: opts.customData,
  };

  try {
    fetch("/api/capi", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      /* échec silencieux : ne jamais bloquer l'expérience */
    });
  } catch {
    /* idem */
  }
}

/**
 * PageView serveur, dédupliqué avec le PageView du Pixel navigateur (même
 * event_id via getPageViewEventId). À appeler au chargement de la LP.
 */
export function sendMetaPageView(userData?: MetaUserData): void {
  sendServerEvent("PageView", { eventId: getPageViewEventId(), userData });
}
